import { Modal } from "react-bootstrap";
import { useState, useEffect } from "react";
import { toast } from "react-toastify";
import jobsApiService from "../services/jobsApiService";
import "../../../css/Offerletter.css";
import masterApi from "../../../services/master.api";
import { useNavigate } from "react-router-dom";
const OfferLetterModal = ({
  show,
  onHide,
  offerData,
  onDecisionSuccess,
  applicationStatus,
  onAcceptanceSuccess,
}) => {
  const [comments, setComments] = useState("");
  const [loading, setLoading] = useState(false);
  const [commentError, setCommentError] = useState("");
  const [medicalLocations, setMedicalLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState("");
  const [isLoadingLocations, setIsLoadingLocations] = useState(false);

  const navigate = useNavigate();
  const [showAcceptanceModal, setShowAcceptanceModal] = useState(false);
  const [isConsentChecked, setIsConsentChecked] = useState(false);

  // DEFINE THIS BEFORE useEffect:
  const isActionableOffer =
    applicationStatus === "OFFERED" || applicationStatus === "OFFER_EXTENDED";

  useEffect(() => {
    if (show) {
      setComments("");
      setSelectedLocation("");
      setIsConsentChecked(false);
    }

    const fetchMedicalLocations = async () => {
      if (!show || !isActionableOffer) return;

      setIsLoadingLocations(true);
      const orgTypes = ["Zonal Office", "Regional Office"];

      try {
        const response = await masterApi.getInterviewCentresByState(
          orgTypes,
          "",
        );
        setMedicalLocations(response.data || []);
      } catch (error) {
        toast.error("Failed to load medical locations");
      } finally {
        setIsLoadingLocations(false);
      }
    };

    fetchMedicalLocations();
  }, [show, isActionableOffer]);

  if (!offerData) return null;

  const handleDecision = async (accepted) => {
    if (!accepted && !comments.trim()) {
      setCommentError("Please enter comments before rejecting the offer");
      return;
    }

    if (accepted && !selectedLocation) {
      toast.error("Please select a medical location");
      return;
    }

    try {
      setLoading(true);

      const payload = {
        applicationId: offerData.applicationId,
        status: accepted ? "OFFER_ACCEPTED" : "OFFER_REJECTED",
        medicalCentreId: selectedLocation,
        comments: comments || "",
        consentAccepted: isConsentChecked,
      };

      await jobsApiService.updateOfferDecision(payload);

      setComments("");

      if (accepted) {
        onHide();
        onAcceptanceSuccess?.();
      } else {
        toast.success("Offer rejected successfully");
        onDecisionSuccess?.();
        onHide();
      }
    } catch (err) {
      toast.error("Failed to submit decision");
    } finally {
      setLoading(false);
    }
  };

  // Only show action UI (consent, medical selection, buttons) for active offer statuses

  const modalHeight = isActionableOffer ? "57vh" : "75vh";

  const targetExpiryDate = offerData?.isExtended
    ? offerData?.extensionDate
    : offerData?.acceptBeforeDate;

  const isAcceptanceExpired = (() => {
    if (!targetExpiryDate) return false;

    const today = new Date();
    const acceptDate = new Date(targetExpiryDate);

    today.setHours(0, 0, 0, 0);
    acceptDate.setHours(23, 59, 59, 999);

    return today > acceptDate;
  })();

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="lg"
      centered
      dialogClassName="offer-modal"
    >
      <Modal.Header className="py-3 px-3" closeButton>
        <Modal.Title className="lettertitle">Offer Letter</Modal.Title>
      </Modal.Header>

      <Modal.Body style={{ height: modalHeight, padding: 0 }}>
        {offerData?.offerLetterUrl ? (
          <iframe
            src={offerData.offerLetterUrl}
            title="Offer Letter"
            width="100%"
            height="100%"
            style={{ border: "none" }}
          />
        ) : (
          <div className="d-flex justify-content-center align-items-center h-100">
            <span className="text-muted">Loading offer letter…</span>
          </div>
        )}
      </Modal.Body>
      {isActionableOffer && (
        <div
          className="card shadow-sm mx-3 mt-2 mb-2"
          style={{
            border: "1px solid #f26623",
            borderLeft: "5px solid #f26623",
            backgroundColor: "#fff8f4",
            borderRadius: "8px",
          }}
        >
          <div className="card-body py-2 px-3">
            <div className="form-check mb-0">
              <input
                className="form-check-input"
                type="checkbox"
                id="offerConsent"
                checked={isConsentChecked}
                onChange={(e) => {
                  const checked = e.target.checked;

                  setIsConsentChecked(checked);

                  if (!checked) {
                    setSelectedLocation("");
                  }
                }}
                style={{
                  cursor: "pointer",
                  borderColor: "#f26623",
                }}
              />

              <label
                className="form-check-label ms-2"
                htmlFor="offerConsent"
                style={{
                  color: "#444",
                  cursor: "pointer",
                  lineHeight: "1.4",
                  fontSize: "14px",
                }}
              >
                I hereby accept the above offer of appointment in the Bank's
                service and agree to abide by all the terms and conditions
                stipulated in this offer letter.
              </label>
            </div>

            {!isConsentChecked && (
              <div
                className="mt-2"
                style={{
                  color: "#d35400",
                  fontSize: "13px",
                }}
              >
                <i className="bi bi-info-circle-fill me-2 text-warning"></i>
                Please provide your consent to enable{" "}
                <strong>Medical Location</strong> selection and the{" "}
                <strong>Accept</strong> button.
              </div>
            )}
          </div>
        </div>
      )}

      {isActionableOffer && (
        <Modal.Footer className="d-block">
          <div className="container-fluid px-0">
            <div className="row gx-3">
              <div className="col-md-6">
                <div className="mb-3">
                  <label htmlFor="medicalLocation" className="form-label">
                    Medical Location
                  </label>{" "}
                  <span className="text-danger">*</span>
                  <select
                    id="medicalLocation"
                    className="form-select"
                    value={selectedLocation}
                    onChange={(e) => setSelectedLocation(e.target.value)}
                    disabled={!isConsentChecked || isLoadingLocations}
                  >
                    <option value="">Select Medical Location</option>
                    {medicalLocations.map((location) => (
                      <option
                        key={location.interviewCentreId}
                        value={location.interviewCentreId}
                      >
                        {location.displayName}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="col-md-6">
                <div className="mb-3 h-100 d-flex flex-column">
                  <label htmlFor="comments" className="form-label">
                    Comments
                  </label>
                  <textarea
                    id="comments"
                    className="form-control flex-grow-1"
                    placeholder="Enter comments..."
                    value={comments}
                    onChange={(e) => {
                      setComments(e.target.value);
                      if (commentError) setCommentError("");
                    }}
                  />
                  {commentError && (
                    <div className="invalid-feedback d-block">
                      {commentError}
                    </div>
                  )}
                </div>
              </div>
            </div>
            {isAcceptanceExpired && (
              <div className="mb-2">
                <div className="alert alert-danger py-2 mb-0">
                  Acceptance/Rejection date expired on{" "}
                  {new Date(targetExpiryDate).toLocaleDateString("en-GB")}
                </div>
              </div>
            )}
            <div className="d-flex justify-content-end gap-2 mt-2">
              <span
                style={{
                  cursor:
                    loading || isAcceptanceExpired || isConsentChecked
                      ? "not-allowed"
                      : "pointer",
                }}
                title={
                  isConsentChecked
                    ? "Uncheck consent to reject the offer."
                    : isAcceptanceExpired
                      ? "Acceptance/Rejection period has expired."
                      : ""
                }
              >
                <button
                  className="btn btn-danger px-4"
                  disabled={loading || isAcceptanceExpired || isConsentChecked}
                  onClick={() => handleDecision(false)}
                >
                  Reject
                </button>
              </span>

              <span
                style={{
                  cursor:
                    loading ||
                    isAcceptanceExpired ||
                    !isConsentChecked ||
                    !selectedLocation
                      ? "not-allowed"
                      : "pointer",
                }}
                title={
                  !isConsentChecked
                    ? "Please provide consent first."
                    : !selectedLocation
                      ? "Please select a medical location."
                      : isAcceptanceExpired
                        ? "Acceptance period has expired."
                        : ""
                }
              >
                <button
                  className="btn btn-success px-4"
                  disabled={
                    loading ||
                    isAcceptanceExpired ||
                    !isConsentChecked ||
                    !selectedLocation
                  }
                  onClick={() => handleDecision(true)}
                >
                  Accept
                </button>
              </span>
            </div>
          </div>
        </Modal.Footer>
      )}
    </Modal>
  );
};

export default OfferLetterModal;
